import {
  DeleteCacheValue,
  RolodexContext,
} from 'features/rolodex/RolodexProvider'
import indexOf from 'lodash/indexOf'
import merge from 'lodash/merge'
import React, { useEffect, useMemo } from 'react'
import {
  RolodexEntryJson,
  RolodexEntryPatchJson,
  RolodexEntryPutJson,
} from 'types'

/**
 * A single instance of a Rolodex cache (which is capable of pushing updates
 * to Rolodex Provider's list of caches, which itself affects all useRolodex
 * hooks throughout the application.
 */
export interface RolodexCacheInstance {
  /**
   * Performs a fake /entries "PUT" into this Rolodex cache
   * @param entryJson     The "PUT" body, or undefined (no-op)
   */
  putEntry(entryJson: RolodexEntryPutJson | undefined): void

  /**
   * Performs a fake /entries "PATCH" into this Rolodex cache. Note that
   * patches can be applied on top of one another, just like with Rolodex.
   * @param entryJson     The "PATCH" body, or undefined (no-op)
   */
  patchEntry(entryJson: RolodexEntryPatchJson | undefined): void

  /**
   * Performs a fake /entries "DELETE" in this Rolodex cache.
   * @param id            The id of the entry to delete, or undefined (no-op)
   */
  deleteEntry(id: string | undefined): void

  /**
   * Resets all cached /entries operations (PUT, PATCH, DELETE) for a single
   * entry in this Rolodex cache instance.
   * @param id            The id of the entry to reset, or undefined (no-op)
   */
  resetEntry(id: string | undefined): void

  /**
   * Resets all cached /entries operations (PUT, PATCH, DELETE) for all entries
   * in this Rolodex cache instance.
   */
  resetEntries(): void

  // /**
  //  * Performs a fake /files "PUT" in this Rolodex cache.
  //  * @param filePath      The path to this file (including its entry ID)
  //  * @param getBlobFn     A function to get a Blob for this file
  //  */
  // putFile(filePath: RolodexFilePath, getBlobFn: () => Promise<Blob>): void
  //
  // /**
  //  * Performs a fake /files "DELETE" in this Rolodex cache.
  //  * @param filePath      The path to this file (including its entry ID)
  //  */
  // deleteFile(filePath: RolodexFilePath): void
  //
  // /**
  //  * Resets all cached /files operations (PUT, DELETE) for all files in this
  //  * Rolodex cache instance.
  //  */
  // clearFiles(): void

  /**
   * Clears out all cached operations for entries and files in this Rolodex
   * cache instance.
   */
  clearAll(): void
}

/**
 * A hook for interacting with a local Rolodex entry cache, used for simulating
 * updates to the underlying entries - particularly in the case where those
 * updates might later be pushed to Rolodex.
 *
 * We store a hierarchy of caches, one pre call to this hook (using React.useId()).
 * When this hook unmounts, its cache entries are cleared. When IDs conflict
 * between sub-caches, we merge the entries, with the more recently added caches
 * taking precedence.
 */
export const useRolodexCache = (): RolodexCacheInstance => {
  //----------------------------------------------------------------------------

  const cacheId = React.useId()

  const {
    setEntryCache,
    // setFileCache
  } = React.useContext(RolodexContext)

  // const createFileKey = useCallback((entry_id: string, path: string) => {
  //   return JSON.stringify({ entry_id, path })
  // }, [])

  const putEntry = React.useCallback(
    (entryJson: RolodexEntryJson | undefined) => {
      if (entryJson)
        setEntryCache((prevCache) => {
          // Check to see if we're putting something in the cache that's
          // already there. This is to avoid an infinite loop in the cases
          // where we're caching something that depends on other Rolodex entries.
          // Don't use 'thisIdCache' below for this check - it's a deep copy,
          // so you want to avoid doing the copy if there is a match.
          if (
            JSON.stringify(prevCache.get(cacheId)?.get(entryJson.id)) ===
            JSON.stringify(entryJson)
          ) {
            return prevCache
          }
          const [newCache, thisIdCache] = getCopyOrSetDefault(
            prevCache,
            cacheId,
            createEntryCache,
            (v) => new Map(v)
          )
          const newEntry = merge(
            {
              generation: new Date().getTime(),
            },
            entryJson
          )

          thisIdCache.set(entryJson.id, newEntry)
          return newCache
        })
    },
    [cacheId, setEntryCache]
  )
  const patchEntry = React.useCallback(
    (entryPatchJson: RolodexEntryPatchJson) =>
      setEntryCache((prevCache) => {
        // Same logic for this check as above in 'putEntry'.
        if (
          JSON.stringify(prevCache.get(cacheId)?.get(entryPatchJson.id)) ===
          JSON.stringify(entryPatchJson)
        ) {
          return prevCache
        }
        const [newCache, thisIdCache] = getCopyOrSetDefault(
          prevCache,
          cacheId,
          createEntryCache,
          (v) => new Map(v)
        )
        // Don't move this up - 'thisIdCache' is a copy
        const previousPatch = thisIdCache.get(entryPatchJson.id)
        if (previousPatch === DeleteCacheValue) return prevCache

        const mergedPatches = { id: entryPatchJson.id }
        merge(
          mergedPatches,
          previousPatch,
          {
            generation: new Date().getTime(),
          },
          entryPatchJson
        )
        thisIdCache.set(entryPatchJson.id, mergedPatches)
        return newCache
      }),
    [cacheId, setEntryCache]
  )
  const deleteEntry = React.useCallback(
    (id: string) =>
      setEntryCache((prevCache) => {
        const [newCache, thisIdCache] = getCopyOrSetDefault(
          prevCache,
          cacheId,
          createEntryCache,
          (v) => new Map(v)
        )
        thisIdCache.set(id, DeleteCacheValue)
        return newCache
      }),
    [cacheId, setEntryCache]
  )
  const resetEntry = React.useCallback(
    (id: string) => {
      setEntryCache((prevCache) => {
        const [newCache, thisIdCache] = getCopyOrSetDefault(
          prevCache,
          cacheId,
          createEntryCache,
          (v) => new Map(v)
        )
        thisIdCache.delete(id)
        return newCache
      })
    },
    [setEntryCache, cacheId]
  )
  const resetEntries = React.useCallback(
    () =>
      setEntryCache((prevCache) => {
        const [newCache] = getCopyOrSetDefault(
          prevCache,
          cacheId,
          createEntryCache,
          (v) => new Map(v)
        )
        newCache.delete(cacheId)
        return newCache
      }),
    [setEntryCache, cacheId]
  )

  //----------------------------------------------------------------------------
  // This file code is not used -- if you ever want to use it, uncomment it, add generation number support, and message
  // the FE-Monorepo team (or whatever team owns the FE after a re-org).

  // const putFile = React.useCallback(
  //   (filePath: RolodexFilePath, getBlobFn: () => Promise<Blob>) => {
  //     setFileCache((prev) => {
  //       const [newCache, thisIdCache] = getCopyOrSetDefault(
  //         prev,
  //         cacheId,
  //         () => new Map<string, FileCacheValueType>(),
  //         (v) => new Map(v)
  //       )
  //       const fileKey = createFileKey(filePath.entry_id, filePath.path)
  //       // If there is already a file function at this key, clear up any memory associated with the Blob URL
  //       releaseBlobUrlForFile(thisIdCache, fileKey)
  //
  //       // Set the new get-blob-url function
  //       thisIdCache.set(
  //         fileKey,
  //         memoize(async () => {
  //           const blob = await getBlobFn()
  //           return URL.createObjectURL(blob)
  //         })
  //       )
  //       return newCache
  //     })
  //   },
  //   [cacheId, createFileKey, setFileCache]
  // )
  //
  // const deleteFile = React.useCallback(
  //   (filePath: RolodexFilePath) => {
  //     setFileCache((prev) => {
  //       const key = createFileKey(filePath.entry_id, filePath.path)
  //       const [newCache, thisIdCache] = getCopyOrSetDefault(
  //         prev,
  //         cacheId,
  //         () => new Map<string, FileCacheValueType>(),
  //         (v) => new Map(v)
  //       )
  //       releaseBlobUrlForFile(thisIdCache, key)
  //       thisIdCache.delete(key)
  //       return newCache
  //     })
  //   },
  //   [cacheId, createFileKey, setFileCache]
  // )
  // const clearFiles = React.useCallback(() => {
  //   setFileCache((prev) => {
  //     const [newCache, thisIdCache] = getCopyOrSetDefault(
  //       prev,
  //       cacheId,
  //       () => new Map<string, FileCacheValueType>(),
  //       (v) => new Map(v)
  //     )
  //     Array.from(thisIdCache.keys()).forEach((key) => {
  //       releaseBlobUrlForFile(thisIdCache, key)
  //     })
  //     newCache.delete(cacheId)
  //     return newCache
  //   })
  // }, [cacheId, setFileCache])

  //----------------------------------------------------------------------------

  const clearAll = React.useCallback(() => {
    resetEntries()
    // clearFiles()
  }, [resetEntries])

  useEffect(() => {
    // remove this cache on unmount
    return clearAll
  }, [clearAll])

  //----------------------------------------------------------------------------

  return useMemo(
    () => ({
      // Entry cache
      putEntry,
      patchEntry,
      deleteEntry,
      resetEntry,
      resetEntries,
      // File cache
      // putFile,
      // deleteFile,
      // clearFiles,
      // Other
      clearAll,
    }),
    [
      putEntry,
      patchEntry,
      deleteEntry,
      resetEntry,
      resetEntries,
      // putFile,
      // deleteFile,
      // clearFiles,
      clearAll,
    ]
  )
}

// This is used to update sub-caches
// We get the value under 'key' from 'map', and return a copy of it,
// creating a default and inserting a default if it wasn't present.
// We return a copy of the value along with a copy of the underlying map,\
// because we might have had to modify it.
// If we re-inserted an existing item, we restore the original order
// according to https://www.geeksforgeeks.org/how-to-sort-a-map-in-javascript/,
// in order to preserve the hierarchical order in RolodexProvider
// Type parameters:
//   - V: the value type in the 'map' parameter
//   - VC: The type of value to create from def() and copy() - this can just be V,
//   but is separated out here to allow us to keep ReadonlyMaps in V, but create plain
//   Map<> that the caller can edit
function getCopyOrSetDefault<V, VC extends V>(
  map: ReadonlyMap<string, V>,
  key: string,
  def: () => VC,
  copy: (val: V) => VC
): [Map<string, V>, VC] {
  const value = map.get(key)
  if (value === null || value === undefined) {
    const newValue = def()
    if (newValue === undefined || newValue === null) {
      throw new Error('default value generator returned null or undefined')
    }
    const newMap = new Map(map).set(key, newValue)
    return [newMap, newValue]
  }
  const valueCopy = copy(value)

  const keyOrdering = [...map.keys()]
  const newMap = new Map(map).set(key, valueCopy)
  return [
    new Map([...newMap.entries()].sort(([key]) => indexOf(keyOrdering, key))),
    valueCopy,
  ]
}

// Creator fo the entry cache - really just used so we don't have
// to repeat the types everywhere
function createEntryCache(): Map<string, RolodexEntryPatchJson | 'deleted'> {
  return new Map()
}

// /**
//  * A function to clear release the URL cached in a lodash.memoize function
//  *    (NOT in the rolodexCache)
//  *
//  * @param localRolodexFileCache - The file-cache associated with this hook's `cacheId`
//  * @param fileKey - The key in the file-cache for the file to release
//  *
//  * @returns true if a URL was found and revoked, false otherwise
//  */
// function releaseBlobUrlForFile(
//   localRolodexFileCache: Map<string, FileCacheValueType>,
//   fileKey: string
// ) {
//   const lodashMemoBlobUrlFn = localRolodexFileCache.get(fileKey)
//   if (lodashMemoBlobUrlFn?.cache) {
//     // this accesses the cache of this specific memoized function (not a global one)
//     const urlInFnCache = lodashMemoBlobUrlFn.cache.get(undefined)
//     if (typeof urlInFnCache === 'string') {
//       URL.revokeObjectURL(urlInFnCache)
//       return true
//     }
//   }
//   return false
// }
